// src/plugins/sales-forecast/components/RangeSliderControl.tsx
import React from "react";
import { Range } from "react-range";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface RangeSliderControlProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  values: [number, number];
  onChange: (values: number[]) => void;
  formatValue?: (n: number) => string;
}

/**
 * A fully theme-aware range slider control.
 * Uses system theme (light/dark) and CSS variables for colors.
 */
export const RangeSliderControl: React.FC<RangeSliderControlProps> = ({
  label,
  min,
  max,
  step = 1,
  values,
  onChange,
  formatValue = (n) => `${n}`,
}) => {
  const theme = useSystemTheme();

  // Fetch theme colors from CSS variables
  const textColor = "var(--text-color)";
  const accentColor = "var(--accent-color)";
  const borderColor = "var(--border-color)";
  const trackBg =
    "var(--track-bg-color)";

  return (
    <div
      className={`theme-${theme} space-y-2 transition-all duration-300`}
      style={{ color: textColor }}
    >
      {/* Label now theme-aware */}
      <label
        className="block text-sm font-medium"
        style={{ color: textColor }}
      >
        {label}
      </label>

      <div
        className="flex justify-between text-sm"
        style={{ color: textColor }}
      >
        <span>{formatValue(values[0])}</span>
        <span>{formatValue(values[1])}</span>
      </div>

      <Range
        step={step}
        min={min}
        max={max}
        values={values as number[]}
        onChange={onChange}
        renderTrack={({ props, children }) => (
          <div
            {...props}
            style={{
              ...props.style,
              height: "4px",
              borderRadius: "4px",
              background: trackBg,
            }}
          >
            {children}
          </div>
        )}
        renderThumb={({ props }) => (
          <div
            {...props}
            style={{
              ...props.style,
              height: "16px",
              width: "16px",
              borderRadius: "50%",
              backgroundColor: accentColor,
              border: `1px solid ${borderColor}`,
              boxShadow: `0 0 4px ${accentColor}40`,
              transition: "background-color 0.3s, box-shadow 0.3s",
            }}
          />
        )}
      />
    </div>
  );
};